// Backlog 072-A — hidden `_watch <trace_id>` subcommand.
//
// Spawned by `grid plug --detach` as a detached child process. Polls
// the deploy trace until terminal, then fires the OS notification and
// appends a line to ~/.cloudgrid/deploys.log. No stdout output (the child
// has stdio:'ignore' from the parent's perspective anyway).
//
// The verb starts with an underscore so it's clearly internal — users
// who run `cloudgrid --help` won't see it (commander hides it via
// `addCommand(cmd, { hidden: true })` in index.ts).

import { Command } from 'commander';

import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { isTraceId } from '../services/trace-format.js';
import {
  pollDeploy,
  DEFAULT_MAX_DURATION_MS,
  type DeployTrace,
} from '../services/plug-poll.js';
import { osNotify as defaultOsNotify } from '../services/os-notify.js';
import {
  appendDeployLog as defaultAppendDeployLog,
  formatElapsedHuman,
  type DeployLogEntry,
} from '../services/deploys-log.js';
import { resolveBuildId } from '../services/build-log-tail.js';

export interface WatchDeps {
  client?: IV2Client;
  notify?: typeof defaultOsNotify;
  appendDeployLog?: typeof defaultAppendDeployLog;
  /** Test injection — passed through to pollDeploy. */
  pollIntervalMs?: number;
  /** Test injection — passed through to pollDeploy. */
  maxDurationMs?: number;
  /** Test injection — passed through to pollDeploy. */
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/**
 * Inner runner — exported for tests. Swallows every recoverable error so
 * the detached child never crashes loudly. The user already saw the
 * "Deploy queued" message; if the watcher itself dies, the deploys.log
 * line is best-effort.
 */
export async function runWatch(
  traceId: string,
  deps: WatchDeps = {},
): Promise<void> {
  if (!isTraceId(traceId)) {
    // Invalid argv — silently exit. The detached child has no stdio.
    return;
  }

  const now = deps.now ?? Date.now;
  const startedAtMs = now();

  let client: IV2Client;
  try {
    client = deps.client ?? (await getV2Client());
  } catch {
    // No auth → nothing we can do from a detached child. Skip.
    return;
  }

  let final: DeployTrace;
  try {
    final = await pollDeploy(client, traceId, {
      pollIntervalMs: deps.pollIntervalMs ?? 10_000,
      // Backlog 544 Fix 2 — share the one poll budget with the foreground
      // `plug` path (was 30m here vs 10m there on the same poller).
      maxDurationMs: deps.maxDurationMs ?? DEFAULT_MAX_DURATION_MS,
      sleep: deps.sleep,
    });
  } catch {
    // Network lost, timeout, server error — nothing actionable from a
    // background process. The user can `grid status <trace>` later.
    return;
  }

  const notify = deps.notify ?? defaultOsNotify;
  const appendDeployLog = deps.appendDeployLog ?? defaultAppendDeployLog;
  const yamlSlug = final.entity?.name ?? traceId;
  const isSuccess = final.status === 'success';
  const failedStep = final.steps.find((s) => s.status === 'failed');
  const errorCode = final.error?.code ?? null;
  const buildId =
    (final.result as { build_id?: string } | null)?.build_id ??
    resolveBuildId(final).buildId;
  const elapsedMs = computeElapsedMsFromTrace(final, startedAtMs, now());

  const entry: DeployLogEntry = {
    ts: new Date().toISOString(),
    slug: yamlSlug,
    outcome: isSuccess ? 'success' : 'failure',
    step: isSuccess ? null : failedStep?.step ?? final.error?.failed_step ?? null,
    elapsedMs,
    buildId,
    errorCode: isSuccess ? null : errorCode,
  };
  try {
    appendDeployLog(entry);
  } catch {
    // appendDeployLog is best-effort already; this catch is belt-and-braces.
  }

  const elapsed = formatElapsedHuman(elapsedMs);
  const message = isSuccess
    ? `${yamlSlug} live in ${elapsed}`
    : `${yamlSlug} deploy failed (${entry.step ?? 'unknown'}): ${
        errorCode ?? 'UNKNOWN'
      }`;
  try {
    await notify({ title: 'CloudGrid', message });
  } catch {
    // osNotify already swallows; redundant guard for any future regression.
  }
}

/**
 * Prefer the trace's own elapsed (started_at → completed_at) so the
 * notification message reflects the deploy's wall-clock, not the
 * watcher's poll loop. Falls back to the watcher's measurement when
 * the trace hasn't reported timestamps.
 */
function computeElapsedMsFromTrace(
  trace: DeployTrace,
  watcherStartedMs: number,
  nowMs: number,
): number {
  if (trace.started_at && trace.completed_at) {
    const start = Date.parse(trace.started_at);
    const end = Date.parse(trace.completed_at);
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      return end - start;
    }
  }
  return Math.max(0, nowMs - watcherStartedMs);
}

export const watchCommand = new Command('_watch')
  .description('Internal: detached deploy completion watcher (072-A)')
  .argument('<trace_id>', 'Deploy trace id to watch')
  .action(async (traceId: string) => {
    await runWatch(traceId);
  });
